import { useCallback, useEffect, useRef, type RefObject } from "react";

/** How close to the foot of the pane, in pixels, still counts as reading the newest message. */
const FOOT_TOLERANCE_PX = 2;

type PaneFollowsNewestMessageInput = {
  /** The scrolling pane. */
  paneRef: RefObject<HTMLElement | null>;
  /** Everything the pane scrolls. Its size changing is what the pane follows. */
  listRef: RefObject<HTMLElement | null>;
  /** The list is a conversation, newest at the foot. Shared files run newest-first and are not followed. */
  enabled: boolean;
  /**
   * Names the list on screen: the forum, and the filters over it. A new list is opened at its newest
   * message, however the reader left the one before it.
   */
  listKey: string;
};

export type PaneFollowsNewestMessage = {
  /** Stops following, for a scroll the pane makes away from its foot on the reader's behalf. */
  release: () => void;
};

/**
 * Holds a conversation pane at its newest message while what it shows settles, until the reader scrolls
 * away from it (#6138).
 *
 * A single scroll to the foot, made when the messages arrived, could not do this. It was made against
 * whatever the pane held at that moment -- the loading placeholder, while the forum's subscribers were
 * still on their way -- and the messages then drew from the top, on the oldest one. Nor could it
 * follow a list that grew after it: avatars, read marks and the reader's own sends all add height, and
 * a pane that had landed at the foot was left short of it. So the pane follows its list's size instead,
 * while the reader is at the foot. Their own scroll (wheel, touch, keys, the scrollbar) lets go, and
 * coming back to the foot takes hold again.
 */
export function usePaneFollowsNewestMessage(input: PaneFollowsNewestMessageInput): PaneFollowsNewestMessage {
  const { paneRef, listRef, enabled, listKey } = input;
  const followsRef = useRef(true);
  const enabledRef = useRef(enabled);

  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  useEffect(() => {
    followsRef.current = true;
  }, [listKey]);

  useEffect(() => {
    const pane = paneRef.current;
    const list = listRef.current;
    if (!pane || !list) return;

    const holdAtFoot = () => {
      if (!followsRef.current || !enabledRef.current) return;
      pane.scrollTop = pane.scrollHeight - pane.clientHeight;
    };
    const release = () => {
      followsRef.current = false;
    };
    // A press on the pane itself is a press on its scrollbar: a press on a message lands on the message.
    const releaseOnScrollbarPress = (event: PointerEvent) => {
      if (event.target === pane) release();
    };
    const followAtFoot = () => {
      if (pane.scrollHeight - pane.scrollTop - pane.clientHeight <= FOOT_TOLERANCE_PX) followsRef.current = true;
    };

    pane.addEventListener("wheel", release, { passive: true });
    pane.addEventListener("touchmove", release, { passive: true });
    pane.addEventListener("keydown", release);
    pane.addEventListener("pointerdown", releaseOnScrollbarPress);
    pane.addEventListener("scroll", followAtFoot, { passive: true });

    // Without a ResizeObserver (jsdom), the pane keeps only the scrolls it is asked for.
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(holdAtFoot);
    observer?.observe(pane);
    observer?.observe(list);

    return () => {
      observer?.disconnect();
      pane.removeEventListener("wheel", release);
      pane.removeEventListener("touchmove", release);
      pane.removeEventListener("keydown", release);
      pane.removeEventListener("pointerdown", releaseOnScrollbarPress);
      pane.removeEventListener("scroll", followAtFoot);
    };
  }, [listRef, paneRef]);

  const release = useCallback(() => {
    followsRef.current = false;
  }, []);

  return { release };
}
